import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import { assetUrl } from './utils/assets';
import axios from 'axios';
import { API_URL } from './config';
import { log } from './utils/logger';
import { fireFtueBubble } from './utils/analytics';
import { returnToMainMenu } from './utils/itch';

// Components
import StoryPager from './components/StoryPager';
import CardDeck from './components/CardDeck';
import { FullScreenCardMode, FullScreenCardOffer, UpgradeModal } from './components/card-mode';
import SurveyModal from './components/SurveyModal';
import TabletContainer from './components/TabletContainer';
import GameHUD from './components/GameHUD';
import MenuDropdown from './components/MenuDropdown';
import DiceRollPanel from './components/DiceRollPanel';
import ErrorMessage from './components/ErrorMessage';
import LandingPage from './components/LandingPage';
import AudioConsentPage from './components/AudioConsentPage';
import BugReportModal from './components/BugReportModal';

// Game Flow
import { GameFlowProvider, useGameFlow } from './machines/GameFlowContext';
import { NarrationProvider } from './context/NarrationContext';
import { SoundEffectsProvider, useSoundEffects } from './context/SoundEffectsContext';
import { BackgroundMusicProvider } from './context/BackgroundMusicContext';
import './styles/GameFlow.css';
import './styles/LoadingIndicator.css';
import './styles/Typography.css';
import './styles/TabletLayout.css';
import './styles/Responsive.css';

// Tutorial Components
import TutorialBubble from './tutorial/TutorialBubbleWithProps';

// Types
import {
  SuggestedPhrases, StorySection as StorySectionType,
  StoryInitializeResponse, TutorialResponse,
  SuggestedPhrasesRequest, SuggestedPhrasesResponse, CardPhrase,
  AcceptedCardsRequest, AcceptedCardsResponse, EffectStatus
} from './types';

// API functions
import { playParadoxCard } from './api/storyApi';

// Lazy-loaded (heavy text content, rarely opened)
const PrivacyPolicyModal = React.lazy(() => import('./components/PrivacyPolicyModal'));
const TermsModal = React.lazy(() => import('./components/TermsModal'));

interface AppContentProps {
    hasExistingSession?: boolean;
}

// Inner App component with access to game flow context
const AppContent: React.FC<AppContentProps> = ({ hasExistingSession = false }) => {
    // Game flow state
    const {
      sessionId,
      deckStatus,
      storySections,
      showCardOfferModal,
      showStoryContent,
      storyFadeIn,
      showContinueButton,
      showCards,
      isInitializing,
      cardOffer,
      isAwaitingResponse,
      gameMode,
      tutorialBubble,
      tutorialContext,
      showSurveyModal,
      surveyOrigin,
      demoEndAnswers,
      // Dice flow state
      pendingDiceRoll,
      showDicePanel,
      diceAnimationComplete,
      afterRollData,
      afterRollError,
      isInDiceFlow,
      isShowingFirstParagraph,
      isRollingDice,
      isAwaitingBranchSections,
      isError,
      error,
      // Handlers
      handleBackendResponse,
      handleDeckUpdated,
      handleCardsAccepted,
      handleCardsSkipped,
      handleCardActionSubmitted,
      handleContinueStory,
      handleTutorialContinue,
      handleTutorialDismiss,
      handleCardPlayModeChange,
      handleOpenSurvey,
      handleCloseSurvey,
      // Dice flow handlers
      handleLetsRollClicked,
      handleDiceAnimationComplete,
      // Tutorial handlers
      handleTypingComplete,
      handleDismissError,
      handleBackendError,
      handleRetryAction,
      canRetry,
      // Full-screen card mode
      isFullScreenCardMode,
      handleExitCardMode,
      handleEnterCardMode,
      // Upgrade system
      pendingUpgrade,
      playedCard,
      showUpgradeModal,
      handleUpgradeSelected,
      isAnimatingAsterisk,
      // Turn limit state
      turnsRemaining,
      turnLimit,
      // Landing page
      showLandingPage,
      landingSavedSessions,
      storageAvailable,
      handleStartNewGame,
      handleResumeGame,
      handleReplaceOldestAndStart,
      showAudioConsent,
      handleAudioConsentGiven,
      variant,
    } = useGameFlow();

    // Sound effects state - wait for audio to load before showing content
    const { state: sfxState } = useSoundEffects();
    const isAudioLoaded = sfxState.isLoaded;

    // Local state for additional data
    const [suggestedPhrases, setSuggestedPhrases] = useState<SuggestedPhrases>({
        card_phrases: {}
    });

    // Paradox card state
    const [activeEffects, setActiveEffects] = useState<EffectStatus[]>([]);
    const [isParadoxLoading, setIsParadoxLoading] = useState(false);

    // Debug state
    const [currentPuzzle, setCurrentPuzzle] = useState<string>('');
    const [currentPuzzleSteps, setCurrentPuzzleSteps] = useState<number>(0);

    // Menu and Privacy Policy state
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [isPrivacyPolicyOpen, setIsPrivacyPolicyOpen] = useState<boolean>(false);
    const [isTermsOpen, setIsTermsOpen] = useState<boolean>(false);

    // Bug report modal state
    const [isBugReportOpen, setIsBugReportOpen] = useState<boolean>(false);


    // Pager-based navigation replaces scroll-based auto-centering

    // Tutorial step 4 uses dice flow endpoint, earlier steps use tutorial endpoint
    const shouldUseTutorialEndpoint = gameMode === 'tutorial' && tutorialContext?.backendStep !== 4;

    // Determine whether to show cards - the game flow machine controls this
    const showCardMechanics = showCards;

    // Find the most recent chapter_start section for TopBar and Mission info
    const chapterStartSection = storySections
        .filter(section => section.section_type === 'chapter_start')
        .slice(-1)[0]; // Get the last chapter_start section

    // Use chapter info from story sections if available, otherwise show tutorial defaults
    const chapterNumber = chapterStartSection?.chapter_info?.number;
    const chapterTitle = chapterStartSection?.chapter_info?.title || 'Introduction';
    const chapterThumbnail = chapterStartSection?.chapter_info?.thumbnail;
    const currentMission = chapterStartSection?.chapter_info?.mission;

    // Tutorial is completed if we have a real chapter with a mission
    const tutorialCompleted = !!currentMission;

    const currentSection = storySections[storySections.length - 1];
    const isOnSplashPage = false;

    // Scroll effects removed in favor of pager navigation

    // Tutorial bubbles are now managed automatically by the game flow machine

    // Function to fetch suggested phrases from the backend
    const fetchSuggestedPhrases = useCallback(async (cardNames: string[]) => {
        log.debug('[fetchSuggestedPhrases] Called with sessionId:', sessionId, 'cardNames:', cardNames);
        if (!sessionId) {
            log.debug('[fetchSuggestedPhrases] Skipping - no sessionId');
            return;
        }

        try {
            const requestData: SuggestedPhrasesRequest = {
                session_id: sessionId,
                card_names: cardNames
            };
            log.debug('[fetchSuggestedPhrases] Making request with:', requestData);

            const response = await axios.post<SuggestedPhrasesResponse>(
                `${API_URL}/suggested_phrases`,
                requestData
            );

            // Convert the backend response format to our frontend format
            const phrasesMap: { [key: string]: string[] } = {};

            response.data.available_cards.forEach((cardPhrase: CardPhrase) => {
                if (cardPhrase.card_phrases) {
                    phrasesMap[cardPhrase.card_name] = cardPhrase.card_phrases;
                } else {
                    phrasesMap[cardPhrase.card_name] = [];
                }
            });

            setSuggestedPhrases({ card_phrases: phrasesMap });
        } catch (error) {
            console.error('Error fetching suggested phrases:', error);
        }
    }, [sessionId]);

    // Fetch suggested phrases whenever the deck status changes
    useEffect(() => {
        if (tutorialBubble?.show && tutorialBubble.key) {
            fireFtueBubble(tutorialBubble.key);
        }
    }, [tutorialBubble?.show, tutorialBubble?.key]);

    useEffect(() => {
        log.debug('[Suggested phrases effect] sessionId:', sessionId, 'deckStatus:', deckStatus);
        if (sessionId && deckStatus && deckStatus.hand.length > 0) {
            const cardNames = deckStatus.hand.map(card => card.name);
            fetchSuggestedPhrases(cardNames);
        }
    }, [sessionId, deckStatus, fetchSuggestedPhrases]);

    // Handle accepting selected cards
    const handleAcceptCardsWithAPI = async (selectedCardNames: string[]) => {
        if (!sessionId || !cardOffer) return;

        try {
            const requestData: AcceptedCardsRequest = {
                session_id: sessionId,
                selected_card_names: selectedCardNames
            };

            const response = await axios.post<AcceptedCardsResponse>(
                `${API_URL}/accepted_cards`,
                requestData
            );

            // Update game flow with new deck status
            handleCardsAccepted(selectedCardNames);
            
            // Update local deck status for suggested phrases
            if (response.data.deck_status) {
                // Fetch new suggested phrases for updated hand
                const cardNames = response.data.deck_status.hand.map(card => card.name);
                fetchSuggestedPhrases(cardNames);
            }
        } catch (error) {
            console.error('Error accepting cards:', error);
        }
    };

    // Handle skipping card offering
    const handleSkipCardOffering = async () => {
        await handleAcceptCardsWithAPI([]);
        handleCardsSkipped();
    };

    const handleFeedbackClick = () => {
        handleOpenSurvey('feedback-button');
    };

    const handleCardActionSubmit = async (cardName: string, actionPhrase: string) => {
        // Signal to game flow that action was submitted
        // The state machine handles all API calls based on current mode:
        // - Tutorial steps 1-3: awaitingResponse state, this function calls /tutorial/ endpoint
        // - Tutorial step 4 and normal mode: animatingAsterisk -> awaitingInitiateResponse (calls /continue-story/initiate)
        handleCardActionSubmitted(cardName, actionPhrase);

        // Tutorial steps 1-3 use old endpoint (called from here)
        // Tutorial step 4 and normal mode use upgrade flow + dice endpoints (handled by state machine)
        if (shouldUseTutorialEndpoint) {
            try {
                const requestData = {
                    session_id: sessionId,
                    card_name: cardName,
                    action_phrase: actionPhrase
                };

                const response = await axios.post(`${API_URL}/tutorial/`, requestData);

                // Extract tutorial info
                const tutorialResponse = response.data as TutorialResponse;
                const backendStep = tutorialResponse.tutorial_step;
                const tutorialCompletedFlag = tutorialResponse.tutorial_completed;

                // Store puzzle info for debugging
                if (response.data.current_puzzle !== undefined) {
                    setCurrentPuzzle(response.data.current_puzzle);
                }
                if (response.data.current_puzzle_steps !== undefined) {
                    setCurrentPuzzleSteps(response.data.current_puzzle_steps);
                }

                // Update game flow with backend response
                handleBackendResponse(
                    response.data.deck_status,
                    response.data.story_sections,
                    backendStep,
                    tutorialCompletedFlag
                );
            } catch (error) {
                console.error('Error in tutorial endpoint', error);
                handleBackendError((error as Error).message || 'Failed to continue tutorial. Please try again.');
            }
        }
        // For tutorial step 4 and normal mode, the state machine handles everything:
        // animatingAsterisk (calls /play-action) -> awaitingInitiateResponse (calls /continue-story/initiate)
    };

    // Handle playing paradox/junk cards
    const handleParadoxPlay = useCallback(async (cardName: string) => {
        if (!sessionId) return;

        setIsParadoxLoading(true);
        try {
            const response = await playParadoxCard(sessionId, cardName);

            // Update deck status through game flow machine
            handleDeckUpdated(response.deck_status);

            // Update active effects
            setActiveEffects(response.active_effects);

            // Fetch new suggested phrases for updated hand
            const cardNames = response.deck_status.hand.map(card => card.name);
            fetchSuggestedPhrases(cardNames);
        } catch (error) {
            console.error('Error playing paradox card:', error);
            handleBackendError((error as Error).message || 'Failed to play card. Please try again.');
        } finally {
            setIsParadoxLoading(false);
        }
    }, [sessionId, handleDeckUpdated, handleBackendError, fetchSuggestedPhrases]);

    // Clear active effects when dice roll completes (effects are consumed)
    useEffect(() => {
        if (diceAnimationComplete && activeEffects.length > 0) {
            setActiveEffects([]);
        }
    }, [diceAnimationComplete, activeEffects.length]);



    // Memoize onContinue to prevent StoryPager re-renders
    const storyPagerOnContinue = useMemo(
        () => (isShowingFirstParagraph ? handleLetsRollClicked : handleContinueStory),
        [isShowingFirstParagraph, handleLetsRollClicked, handleContinueStory]
    );

    if (showAudioConsent) {
        return <AudioConsentPage onChoiceMade={handleAudioConsentGiven} />;
    }

    if (showLandingPage) {
        // Landing page intentionally bypasses TabletContainer so the full-bleed
        // book cover fills the viewport edge-to-edge (no dark tablet frame).
        return (
            <>
                <LandingPage
                    savedSessions={landingSavedSessions}
                    storageAvailable={storageAvailable}
                    variant={variant}
                    onNewGame={handleStartNewGame}
                    onResumeGame={handleResumeGame}
                    onReplaceOldest={handleReplaceOldestAndStart}
                    onPrivacyPolicyClick={() => setIsPrivacyPolicyOpen(true)}
                    onTermsClick={() => setIsTermsOpen(true)}
                />
                <React.Suspense fallback={null}>
                    <PrivacyPolicyModal
                        isOpen={isPrivacyPolicyOpen}
                        onClose={() => setIsPrivacyPolicyOpen(false)}
                    />
                    <TermsModal
                        isOpen={isTermsOpen}
                        onClose={() => setIsTermsOpen(false)}
                    />
                </React.Suspense>
            </>
        );
    }

    return (
        <TabletContainer>
            {/* Game HUD */}
            <GameHUD
                chapterNumber={chapterNumber}
                chapterTitle={chapterTitle}
                chapterImage={chapterThumbnail ? assetUrl(`/assets/chapters/${chapterThumbnail}`) : undefined}
                turnsRemaining={turnsRemaining ?? undefined}
                turnLimit={turnLimit ?? undefined}
                mission={currentMission || null}
                tutorialCompleted={tutorialCompleted}
                chapterSectionId={chapterStartSection?.section_id}
                hidden={isOnSplashPage}
                onMenuClick={() => setIsMenuOpen(!isMenuOpen)}
            />

            {/* Menu Dropdown */}
            <MenuDropdown
                isOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
                onPrivacyPolicyClick={() => setIsPrivacyPolicyOpen(true)}
                onTermsClick={() => setIsTermsOpen(true)}
                onFeedbackClick={handleFeedbackClick}
                onBugReportClick={() => setIsBugReportOpen(true)}
                onMainMenuClick={() => returnToMainMenu()}
            />
            
            {/* Main Content Area */}
            <div className="main-content">
                {/* Full-Screen Card Mode */}
                {isFullScreenCardMode && (
                    <FullScreenCardMode
                        deckStatus={deckStatus || { draw_pile: [], discard_pile: [], hand: [], was_shuffled: false }}
                        suggestedPhrases={suggestedPhrases}
                        isLoading={isAwaitingResponse}
                        onSubmitCardAction={handleCardActionSubmit}
                        onBackToStory={handleExitCardMode}
                        isInFreeFormStep={tutorialCompleted || tutorialContext?.backendStep === 4}
                        isTutorialMode={!tutorialCompleted}
                        tutorialStep={tutorialContext?.backendStep}
                        onCardPlayModeChange={handleCardPlayModeChange}
                        onTypingComplete={handleTypingComplete}
                        isAnimatingAsterisk={isAnimatingAsterisk}
                        activeEffects={activeEffects}
                        onParadoxPlay={handleParadoxPlay}
                        isParadoxLoading={isParadoxLoading}
                    />
                )}

                {/* Loading State - also wait for audio to load */}
                {(!showStoryContent || !isAudioLoaded) && !isError && !isFullScreenCardMode && (
                    <div className="story-area loading">
                        <div className="loading-indicator">
                            <div className="loading-spinner"></div>
                            <p className="small-text">Loading story...</p>
                        </div>
                    </div>
                )}

                {/* Error State */}
                {isError && error && (
                    <div className="story-area">
                        <ErrorMessage
                            error={error}
                            onDismiss={handleDismissError}
                            onRetry={canRetry ? handleRetryAction : undefined}
                            dismissText="Go Back"
                            retryText="Retry"
                        />
                    </div>
                )}

                {/* Story Display Area - Replaced with StoryPager */}
                {showStoryContent && isAudioLoaded && !isError && !isFullScreenCardMode && (
                    <div className="story-area">
                        <StoryPager
                            storySections={storySections}
                            showContinueButton={showContinueButton}
                            onContinue={storyPagerOnContinue}
                            continueButtonText={isShowingFirstParagraph ? "Let's Roll 🎲" : undefined}
                            isAwaitingResponse={isAwaitingResponse || (isAwaitingBranchSections && !afterRollData)}
                            sessionId={sessionId}
                            onOpenSurvey={handleOpenSurvey}
                            skipContinueSound={isShowingFirstParagraph}
                            pendingDiceRoll={isShowingFirstParagraph ? pendingDiceRoll : null}
                            activeEffects={activeEffects}
                        />
                    </div>
                )}

                {/* Card Overlay / Dice Panel - Only show when NOT in full-screen card mode */}
                {!isFullScreenCardMode && (
                    <div className={`card-overlay ${showCardMechanics || showDicePanel ? 'active' : ''}`}>
                        <div className="card-area">
                            {showDicePanel && pendingDiceRoll ? (
                                <DiceRollPanel
                                    diceRollResult={pendingDiceRoll.diceRollResult}
                                    onAnimationComplete={handleDiceAnimationComplete}
                                    showResult={isAwaitingBranchSections}
                                    showContinueButton={isAwaitingBranchSections && afterRollData !== null}
                                    onContinue={handleContinueStory}
                                    isLoadingContinue={false}
                                    triggeredEffects={pendingDiceRoll.triggeredEffects}
                                />
                            ) : (
                                <CardDeck
                                    deckStatus={deckStatus || { draw_pile: [], discard_pile: [], hand: [], was_shuffled: false }}
                                    suggestedPhrases={suggestedPhrases}
                                    isLoading={isAwaitingResponse}
                                    onSubmitCardAction={handleCardActionSubmit}
                                    isTutorialMode={!tutorialCompleted}
                                    onCardSelected={undefined}
                                    isInFreeFormStep={tutorialCompleted || tutorialContext?.backendStep === 4}
                                    isVisible={showCardMechanics}
                                    onCardPlayModeChange={handleCardPlayModeChange}
                                    tutorialStep={tutorialContext?.backendStep}
                                    onTypingComplete={handleTypingComplete}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
            
            {/* Tutorial bubble overlay - now managed by game flow machine */}
            {tutorialBubble?.show && (
                <TutorialBubble 
                    skipCompletionMessage={hasExistingSession}
                    showBubble={true}
                    bubbleMessage={tutorialBubble.message}
                    bubblePosition={tutorialBubble.position}
                    targetElement={tutorialBubble.targetElement}
                    avatar={tutorialBubble.avatar}
                    bubbleButtonType={tutorialBubble.buttonType}
                    onContinue={handleTutorialContinue}
                    onDismiss={handleTutorialDismiss}
                    readinessStrategy={tutorialBubble.readinessStrategy}
                    delay={tutorialBubble.delay}
                />
            )}

            {/* Card offering screen */}
            {cardOffer && showCardOfferModal && (
                <FullScreenCardOffer
                    cardOffer={cardOffer}
                    onSkip={handleSkipCardOffering}
                    onAcceptCards={handleAcceptCardsWithAPI}
                    canSkip={tutorialCompleted}
                    requireAll={!tutorialCompleted && (cardOffer.max_selectable ?? 0) > 1}
                />
            )}

            {/* Upgrade modal */}
            {showUpgradeModal && pendingUpgrade && playedCard && (
                <UpgradeModal
                    upgradeOffer={pendingUpgrade}
                    triggeringCard={playedCard}
                    isOpen={showUpgradeModal}
                    onSelectUpgrade={handleUpgradeSelected}
                />
            )}

            {/* Survey modal */}
            {showSurveyModal && surveyOrigin && (
                <SurveyModal
                    isOpen={showSurveyModal}
                    sessionId={sessionId}
                    origin={surveyOrigin}
                    demoEndAnswers={demoEndAnswers || undefined}
                    onClose={handleCloseSurvey}
                />
            )}

            {/* Privacy Policy modal */}
            <React.Suspense fallback={null}>
                <PrivacyPolicyModal
                    isOpen={isPrivacyPolicyOpen}
                    onClose={() => setIsPrivacyPolicyOpen(false)}
                />
                <TermsModal
                    isOpen={isTermsOpen}
                    onClose={() => setIsTermsOpen(false)}
                />
            </React.Suspense>

            {/* Bug Report modal */}
            <BugReportModal
                isOpen={isBugReportOpen}
                onClose={() => setIsBugReportOpen(false)}
                sessionId={sessionId}
                chapterNumber={chapterNumber}
            />

        </TabletContainer>
    );
};

// Main app with game flow provider wrapper
const InteractiveStoryApp: React.FC = () => {
    // Check URL for existing session
    const urlParams = new URLSearchParams(window.location.search);
    const existingSessionID = urlParams.get('session_id');
    
    // For existing sessions, we should check if tutorial was completed
    const hasExistingSession = !!existingSessionID;
    
    // Add a debug message 
    log.debug("%c STARTING APP WITH GAME FLOW ", "background: #4CAF50; color: white; font-size: 20px", 
        hasExistingSession ? `Existing: ${existingSessionID}` : "New session");
    
    return (
        <NarrationProvider>
            <SoundEffectsProvider>
                <BackgroundMusicProvider>
                    <GameFlowProvider>
                        <AppContent hasExistingSession={hasExistingSession} />
                    </GameFlowProvider>
                </BackgroundMusicProvider>
            </SoundEffectsProvider>
        </NarrationProvider>
    );
};

export default InteractiveStoryApp;